import React, { useState } from 'react'
import {
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native'
import { Audio } from 'expo-av'
import JocComponent from '@/Components/Components/JocComponent'
import { useGlobal } from '@/Components/Context/globalContext'

const SND_ERROR  = require('@/assets/Sounds/renou error.mp3')
const SND_ENCERT = require('@/assets/Sounds/renou encert.mp3')

const playSound = async (source: any) => {
  try {
    const { sound } = await Audio.Sound.createAsync(source, { shouldPlay: true, volume: 0.8 })
    sound.setOnPlaybackStatusUpdate(status => {
      if ('didJustFinish' in status && status.didJustFinish) sound.unloadAsync()
    })
  } catch {}
}

// 10 verses; each verse is an array of words (punctuation attached to word)
const POEM: string[][] = [
  ['Ikusi', 'dutenian'],
  ['balia', 'ur-mintzian,'],
  ['bi', 'arpoi', 'sartu', 'dizke'],
  ['gogor', 'biotzian;'],
  ['indarrak', 'aplakatu'],
  ['odol', 'ixurtzian,'],
  ['ez', 'da', 'geio', 'esnatuko'],
  ['amasa', 'aitzian,'],
  ['derrepente', 'il', 'zaigu'],
  ['ia', 'illuntzian.'],
]

// 0-based indices; letterIdx ignores punctuation so clues match visible letters.
const KEY_LETTERS = [
  { verseIdx: 2, wordIdx: 1, letterIdx: 3 },  // arp[O]i
  { verseIdx: 3, wordIdx: 1, letterIdx: 7 },  // biotzia[N]
  { verseIdx: 4, wordIdx: 0, letterIdx: 2 },  // in[D]arrak
  { verseIdx: 4, wordIdx: 1, letterIdx: 0 },  // [A]plakatu
  { verseIdx: 5, wordIdx: 1, letterIdx: 3 },  // ixu[R]tzian
  { verseIdx: 6, wordIdx: 3, letterIdx: 4 },  // esna[T]uko
  { verseIdx: 7, wordIdx: 1, letterIdx: 3 },  // ait[Z]ian
  { verseIdx: 8, wordIdx: 2, letterIdx: 1 },  // z[A]igu
]

const KEYWORD = 'HONDARTZA' // H pre-filled, then 8 letters O N D A R T Z A
// One hint per KEY_LETTERS entry, same order, split into two display rows.
const HINTS = ['3-2-4', '4-2-8', '5-1-3', '5-2-1', '6-2-4', '7-4-5', '8-2-4', '9-3-2']

const getLetterIdx = (word: string, charIdx: number) =>
  word.slice(0, charIdx + 1).replace(/[^a-zA-Z]/g, '').length - 1

export default function JocPoema(RouterData: any) {
  const { textRecurs } = useGlobal()
  const [found, setFound] = useState<boolean[]>(() => Array(KEY_LETTERS.length).fill(false))
  const [errors, setErrors] = useState(0)
  const [errorMsg, setErrorMsg] = useState('')

  const punts = Math.max(100 - Math.floor(errors / 3) * 25, 0)

  const navigateToReward = () => {
    RouterData.navigation.replace('Reward', {
      ...RouterData.route.params,
      punts,
    })
  }

  const handleLetterPress = (verseIdx: number, wordIdx: number, letterIdx: number) => {
    const kIdx = KEY_LETTERS.findIndex(
      k => k.verseIdx === verseIdx && k.wordIdx === wordIdx && k.letterIdx === letterIdx
    )
    if (kIdx === -1) {
      playSound(SND_ERROR)
      setErrors(e => e + 1)
      setErrorMsg('Esta letra no es clave')
      setTimeout(() => setErrorMsg(''), 2000)
      return
    }
    if (found[kIdx]) return
    playSound(SND_ENCERT)
    const next = [...found]
    next[kIdx] = true
    setFound(next)
    setErrorMsg('')
    if (next.every(Boolean)) setTimeout(navigateToReward, 800)
  }

  return (
    <JocComponent RouterData={RouterData} setPistesDemanades={setErrors}>
      <View style={styles.panel}>
        {/* Keyword progress */}
        <View style={styles.keywordRow}>
          {KEYWORD.split('').map((letter, i) => {
            const filled = i === 0 || found[i - 1]
            return (
              <View key={i} style={[styles.letterBox, filled && styles.letterBoxFilled]}>
                {filled && <Text style={styles.letterBoxText}>{letter}</Text>}
              </View>
            )
          })}
        </View>

        {/* Hints */}
        <View style={styles.hintsBlock}>
          {[HINTS.slice(0, 4), HINTS.slice(4, 8)].map((row, rowIdx) => (
            <View key={rowIdx} style={styles.hintsRow}>
              {row.map((hint, i) => {
                const idx = rowIdx * 4 + i
                return (
                  <React.Fragment key={idx}>
                    <Text style={[styles.hintsText, found[idx] && styles.hintsTextUsed]}>{hint}</Text>
                    {i < row.length - 1 && <Text style={styles.hintsSep}> · </Text>}
                  </React.Fragment>
                )
              })}
            </View>
          ))}
          <Text style={styles.hintsLabel}>{textRecurs('versopalabraletra')}</Text>
        </View>

        {/* Score and error */}
        <View style={styles.scoreRow}>
          <Text style={styles.scoreText}>{punts} {textRecurs('general_puntos').toLowerCase()}</Text>
          {!!errorMsg && <Text style={styles.errorText}>{errorMsg}</Text>}
        </View>

        {/* Poem */}
        <ScrollView style={styles.poemScroll} showsVerticalScrollIndicator={false}>
          {POEM.map((verse, verseIdx) => (
            <View key={verseIdx} style={styles.verseRow}>
              <Text style={styles.verseNum}>{verseIdx + 1}/</Text>
              <View style={styles.verseLine}>
                {verse.map((word, wordIdx) => (
                  <React.Fragment key={wordIdx}>
                    <View style={styles.wordRow}>
                      {word.split('').map((char, charIdx) => {
                        const isAlpha = /[a-zA-Z]/.test(char)
                        const letterIdx = getLetterIdx(word, charIdx)
                        const kIdx = KEY_LETTERS.findIndex(
                          k => k.verseIdx === verseIdx && k.wordIdx === wordIdx && k.letterIdx === letterIdx
                        )
                        const isFound = kIdx !== -1 && found[kIdx]

                        if (isAlpha) {
                          return (
                            <TouchableOpacity
                              key={charIdx}
                              style={styles.charWrap}
                              onPress={() => handleLetterPress(verseIdx, wordIdx, letterIdx)}
                              activeOpacity={0.6}
                              hitSlop={{ top: 10, bottom: 10, left: 4, right: 4 }}
                            >
                              <Text style={[styles.char, isFound && styles.charFound]}>
                                {char.toLowerCase()}
                              </Text>
                            </TouchableOpacity>
                          )
                        }

                        return (
                          <View key={charIdx} style={styles.charWrap}>
                            <Text style={styles.char}>{char}</Text>
                          </View>
                        )
                      })}
                    </View>
                    {wordIdx < verse.length - 1 && (
                      <View style={styles.charWrap}>
                        <Text style={styles.char}> </Text>
                      </View>
                    )}
                  </React.Fragment>
                ))}
              </View>
            </View>
          ))}
          <View style={{ height: 40 }} />
        </ScrollView>
      </View>
    </JocComponent>
  )
}

const styles = StyleSheet.create({
  panel: {
    backgroundColor: '#f9f7e8',
    borderTopEndRadius: 44,
    borderTopStartRadius: 44,
    paddingTop: 16,
    flexGrow: 0,
    flexShrink: 0,
  },
  keywordRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 3,
    paddingHorizontal: 12,
    marginBottom: 8,
  },
  letterBox: {
    width: 30,
    height: 34,
    borderWidth: 2,
    borderColor: '#555',
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#fff',
  },
  letterBoxFilled: {
    backgroundColor: '#00491F',
    borderColor: '#00491F',
  },
  letterBoxText: {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 14,
    fontFamily: 'Poppins',
  },
  hintsBlock: {
    paddingHorizontal: 16,
    marginBottom: 6,
    alignItems: 'center',
  },
  hintsRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  hintsText: {
    fontSize: 12,
    fontFamily: 'Poppins',
    color: '#333',
    fontWeight: '600',
    textAlign: 'center',
  },
  hintsTextUsed: {
    color: '#fff',
    backgroundColor: '#333',
    borderRadius: 3,
    paddingHorizontal: 3,
    overflow: 'hidden',
  },
  hintsSep: {
    fontSize: 12,
    fontFamily: 'Poppins',
    color: '#333',
  },
  hintsLabel: {
    fontSize: 10,
    color: '#888',
    fontFamily: 'Poppins',
    marginTop: 2,
  },
  scoreRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 12,
    marginBottom: 6,
    minHeight: 24,
  },
  scoreText: {
    fontSize: 14,
    fontWeight: 'bold',
    fontFamily: 'Poppins',
    color: '#333',
  },
  errorText: {
    color: '#c0392b',
    fontSize: 12,
    fontFamily: 'Poppins',
  },
  poemScroll: {
    flexGrow: 0,
    flexShrink: 1,
    paddingHorizontal: 16,
  },
  verseRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 2,
  },
  verseNum: {
    fontSize: 11,
    color: '#aaa',
    width: 22,
    paddingTop: 4,
    fontFamily: 'Poppins',
  },
  verseLine: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    flex: 1,
    alignItems: 'center',
  },
  wordRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  charWrap: {
    height: 28,
    justifyContent: 'center',
  },
  char: {
    fontSize: 17,
    fontFamily: 'Poppins',
    color: '#222',
    paddingHorizontal: 1,
  },
  charFound: {
    color: '#00491F',
    fontWeight: 'bold',
    textDecorationLine: 'underline',
  },
})
